/**
 * 計算フィールド 計算エンジン（#3145）の依存解決。
 * 依存抽出・トポロジカルソート・循環参照検知・深さ上限チェックを行う（設計書 §4.5）。
 * ⚠️ functions/src/utils/calculation と main/src/utils/calculation は同一内容。
 *    片方だけ変更しないこと（変更時は両方へ反映し、共通テストで一致を確認する）。
 */
import { CALCULATION_MAX_DEPENDENCY_DEPTH, calculationErrorCode } from './constants';
import { CalculationOrderField, CalculationOrderResult } from './types';
import { tokenize } from './tokenizer';

/**
 * 式が参照しているフィールドコードを出現順に返す（重複は除く）。
 * トークン化できない式は参照なしとして扱う
 */
export const extractFieldCodes = (formula: string): string[] => {
  try {
    const codes: string[] = [];
    tokenize(formula).forEach(token => {
      if (token.type === 'fieldRef' && !codes.includes(token.value)) {
        codes.push(token.value);
      }
    });
    return codes;
  } catch {
    return [];
  }
};

/**
 * 計算フィールドの評価順を依存関係のトポロジカルソートで決める。
 * formula を持つ項目を計算フィールドとして扱い、参照元が先に来る順序を返す。
 * 循環参照（自己参照を含む）と依存の深さ上限超過はエラーを返す
 */
export const resolveCalculationOrder = (
  fields: CalculationOrderField[],
): CalculationOrderResult => {
  const calculatedFields = fields.filter(
    field => field.formula !== undefined && field.formula !== '',
  );
  const calculatedCodes = new Set(calculatedFields.map(field => field.fieldCode));

  // 計算フィールド → 依存する計算フィールドの一覧（通常項目への参照は評価順に影響しない）
  const dependencyMap = new Map<string, string[]>();
  calculatedFields.forEach(field => {
    // 参照先をそのまま依存として使う。
    // SUM({明細.金額}) の {明細.金額} も子項目のフィールドコードそのものなので、
    // 「明細の計算列 → それを SUM する単一項目」の順序は自然に解決される（#3358）
    dependencyMap.set(
      field.fieldCode,
      extractFieldCodes(field.formula ?? '').filter(code => calculatedCodes.has(code)),
    );
  });

  // 依存が解決済みの項目から順に取り出す（同順位は入力順を保つ）
  const order: string[] = [];
  const resolved = new Set<string>();
  const remaining = calculatedFields.map(field => field.fieldCode);
  while (remaining.length > 0) {
    const readyIndex = remaining.findIndex(code =>
      (dependencyMap.get(code) ?? []).every(dependency => resolved.has(dependency)),
    );
    if (readyIndex === -1) {
      return {
        order: [],
        error: {
          code: calculationErrorCode.circular,
          message: `循環参照があります: ${remaining.join(', ')}`,
        },
      };
    }
    const code = remaining.splice(readyIndex, 1)[0];
    order.push(code);
    resolved.add(code);
  }

  // 依存の深さ（参照の連鎖の長さ）を評価順に計算して上限を検証する
  const depthMap = new Map<string, number>();
  for (const code of order) {
    const dependencyDepths = (dependencyMap.get(code) ?? []).map(
      dependency => depthMap.get(dependency) ?? 0,
    );
    const depth = 1 + Math.max(0, ...dependencyDepths);
    if (depth > CALCULATION_MAX_DEPENDENCY_DEPTH) {
      return {
        order: [],
        error: {
          code: calculationErrorCode.limitExceeded,
          message: `参照の連鎖は ${CALCULATION_MAX_DEPENDENCY_DEPTH} 段までです: {${code}}`,
        },
      };
    }
    depthMap.set(code, depth);
  }

  return { order, error: null };
};
